import React from 'react';
import type { IconType } from 'react-icons';
import {
  LuAward,
  LuCalendarDays,
  LuChartNoAxesCombined,
  LuClipboardList,
  LuFileChartColumn,
  LuMap,
  LuMapPinned,
  LuMessageCircleQuestion,
  LuMessagesSquare,
  LuShieldCheck,
  LuTrendingUp,
  LuUsersRound,
} from 'react-icons/lu';
import { useTranslation } from '../../localization/useAppTranslation';
import type { SupportedUiLocale } from '../../localization/locales';
import {
  type ConversationSuggestion,
  type GeneratedConversationSuggestionIcon,
} from '../../types/conversationSuggestions';
import type { PromptSuggestionItem } from '../../types/promptSuggestions';
import '../../styles/PromptSuggestions.css';

export { GENERATED_CONVERSATION_SUGGESTION_ICON_IDENTIFIERS } from '../../types/conversationSuggestions';
export type { GeneratedConversationSuggestionIcon } from '../../types/conversationSuggestions';

export type PromptSuggestionPresentation = 'card' | 'chip' | 'list' | 'compact';

interface PromptSuggestionControlProps {
  item: PromptSuggestionItem;
  presentation: PromptSuggestionPresentation;
  disabled?: boolean;
  onSelect: (item: PromptSuggestionItem) => void;
}

const presentationClassNames: Record<
  PromptSuggestionPresentation,
  string
> = {
  card: 'coach-suggestion-control-card',
  chip: 'coach-suggestion-control-chip',
  compact: 'coach-suggestion-control-compact',
  list: 'coach-suggestion-control-list',
};

const suggestionIcons: Record<GeneratedConversationSuggestionIcon, IconType> = {
  award: LuAward,
  calendar: LuCalendarDays,
  'chart-no-axes-combined': LuChartNoAxesCombined,
  'clipboard-list': LuClipboardList,
  'file-chart-column': LuFileChartColumn,
  map: LuMap,
  'map-pinned': LuMapPinned,
  'messages-square': LuMessagesSquare,
  'road-shield': LuShieldCheck,
  'trending-up': LuTrendingUp,
  'users-round': LuUsersRound,
};

const isGeneratedConversationSuggestionIcon = (
  icon: string,
): icon is GeneratedConversationSuggestionIcon => (
  icon in suggestionIcons
);

export const PromptSuggestionControl: React.FC<PromptSuggestionControlProps> = ({
  item,
  presentation,
  disabled = false,
  onSelect,
}) => {
  const { t } = useTranslation('chat');
  const isCard = presentation === 'card';
  const isList = presentation === 'list';
  const SuggestionIcon = isGeneratedConversationSuggestionIcon(item.display.icon)
    ? suggestionIcons[item.display.icon]
    : LuMessageCircleQuestion;

  return (
    <button
      type="button"
      className={`coach-suggestion-control ${presentationClassNames[presentation]}`}
      onClick={() => onSelect(item)}
      disabled={disabled}
      title={presentation === 'chip' ? item.action.prompt_text : item.display.title}
      aria-label={t('suggestions.send', { title: item.display.title })}
    >
      <SuggestionIcon
        className="coach-suggestion-control-icon"
        data-testid={`prompt-suggestion-icon-${item.display.icon}`}
        aria-hidden="true"
      />
      <span className="coach-suggestion-control-copy">
        <span className="coach-suggestion-control-title">
          {presentation === 'chip' || presentation === 'compact'
            ? item.display.short_title
            : item.display.title}
        </span>
        {(isCard || isList) && (
          <span className="coach-suggestion-control-description">
            {item.display.description}
          </span>
        )}
      </span>
    </button>
  );
};

interface PromptSuggestionCollectionProps {
  suggestions: PromptSuggestionItem[];
  disabled?: boolean;
  maxSuggestions?: number;
  onSelect: (item: PromptSuggestionItem) => void;
}

interface PromptSuggestionCardsProps extends PromptSuggestionCollectionProps {
  className?: string;
}

interface PromptSuggestionChipsProps extends PromptSuggestionCollectionProps {
  shuffleKey?: number;
}

const shuffleSuggestions = (
  suggestions: PromptSuggestionItem[],
  shuffleKey: number,
): PromptSuggestionItem[] => {
  if (suggestions.length < 2 || shuffleKey === 0) {
    return suggestions;
  }

  let seed = suggestions.reduce(
    (currentSeed, suggestion) => (
      Math.imul(currentSeed ^ suggestion.id.length, 16777619) >>> 0
    ),
    shuffleKey,
  );
  const shuffledSuggestions = [...suggestions];

  for (let index = shuffledSuggestions.length - 1; index > 0; index -= 1) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const swapIndex = Math.floor((seed / 4294967296) * (index + 1));
    [shuffledSuggestions[index], shuffledSuggestions[swapIndex]] = [
      shuffledSuggestions[swapIndex],
      shuffledSuggestions[index],
    ];
  }

  const hasSameOrder = shuffledSuggestions.every(
    (suggestion, index) => suggestion.id === suggestions[index].id,
  );
  return hasSameOrder
    ? [...shuffledSuggestions.slice(1), shuffledSuggestions[0]]
    : shuffledSuggestions;
};

export const PromptSuggestionCards: React.FC<PromptSuggestionCardsProps> = ({
  suggestions,
  disabled = false,
  maxSuggestions,
  onSelect,
  className,
}) => {
  const { t } = useTranslation('chat');
  const visibleSuggestions = maxSuggestions === undefined
    ? suggestions
    : suggestions.slice(0, maxSuggestions);

  if (visibleSuggestions.length === 0) {
    return null;
  }

  return (
    <div
      className={`coach-suggestion-cards${className ? ` ${className}` : ''}`}
      aria-label={t('suggestions.ariaLabel')}
    >
      {visibleSuggestions.map((item) => (
        <PromptSuggestionControl
          key={`${item.id}:${item.placement.surface}:${item.placement.variant}`}
          item={item}
          presentation="card"
          disabled={disabled}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};

export const PromptSuggestionChips: React.FC<PromptSuggestionChipsProps> = ({
  suggestions,
  disabled = false,
  maxSuggestions,
  onSelect,
  shuffleKey = 0,
}) => {
  const { t } = useTranslation('chat');
  const shuffledSuggestions = React.useMemo(
    () => shuffleSuggestions(suggestions, shuffleKey),
    [shuffleKey, suggestions],
  );
  const visibleSuggestions = maxSuggestions === undefined
    ? shuffledSuggestions
    : shuffledSuggestions.slice(0, maxSuggestions);

  if (visibleSuggestions.length === 0) {
    return null;
  }

  return (
    <div
      className={`coach-suggestion-chips ${shuffleKey > 0 ? 'is-shuffling' : ''}`}
      aria-label={t('suggestions.ariaLabel')}
      data-shuffle-version={shuffleKey}
    >
      {visibleSuggestions.map((item) => (
        <PromptSuggestionControl
          key={`${item.id}:${item.placement.surface}:${item.placement.variant}:${shuffleKey}`}
          item={item}
          presentation="chip"
          disabled={disabled}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};

interface ConversationSuggestionChipsProps {
  suggestions: ConversationSuggestion[];
  responseLocale: SupportedUiLocale;
  disabled?: boolean;
  onSelect: (suggestion: ConversationSuggestion) => void;
}

export const ConversationSuggestionChips: React.FC<ConversationSuggestionChipsProps> = ({
  suggestions,
  responseLocale,
  disabled = false,
  onSelect,
}) => {
  const { t } = useTranslation('chat');

  if (suggestions.length === 0) {
    return null;
  }

  const suggestionSetKey = suggestions.map((suggestion) => suggestion.id).join(':');

  return (
    <div
      key={suggestionSetKey}
      className="coach-suggestion-chips coach-generated-suggestion-chips"
      aria-label={t('suggestions.generatedAriaLabel')}
    >
      {suggestions.map((suggestion, index) => {
        const SuggestionIcon = isGeneratedConversationSuggestionIcon(suggestion.icon)
          ? suggestionIcons[suggestion.icon]
          : LuMessageCircleQuestion;

        return (
          <button
            key={suggestion.id}
            type="button"
            className="coach-suggestion-control coach-suggestion-control-chip"
            onClick={() => onSelect(suggestion)}
            disabled={disabled}
            title={suggestion.prompt_text}
            aria-label={suggestion.label}
            style={{ animationDelay: `${80 + index * 80}ms` }}
          >
            <SuggestionIcon
              className="coach-suggestion-control-icon"
              data-testid={`conversation-suggestion-icon-${suggestion.icon}`}
              aria-hidden="true"
            />
            <span className="coach-suggestion-control-copy">
              <span className="coach-suggestion-control-title" lang={responseLocale}>
                {suggestion.label}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const PromptSuggestionCompactList: React.FC<PromptSuggestionCollectionProps> = ({
  suggestions,
  disabled = false,
  onSelect,
}) => {
  const { t } = useTranslation('chat');
  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="coach-suggestion-compact-list" aria-label={t('suggestions.ariaLabel')}>
      {suggestions.map((item) => (
        <PromptSuggestionControl
          key={`${item.id}:${item.placement.surface}:${item.placement.variant}`}
          item={item}
          presentation="compact"
          disabled={disabled}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};
